/** Readable text colour in the active theme's accent hue. */

const MIN_CONTRAST = 4.5;

function channel(c) {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function luminance(hex) {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return null;
  const n = parseInt(match[1], 16);
  return (
    0.2126 * channel((n >> 16) & 255)
    + 0.7152 * channel((n >> 8) & 255)
    + 0.0722 * channel(n & 255)
  );
}

export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  if (la == null || lb == null) return 0;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

function parseHsl(value) {
  const match = String(value || '').trim().match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!match) return null;
  return { h: Number(match[1]), s: Number(match[2]), l: Number(match[3]) };
}

export function hexFromHsl(value) {
  const parsed = parseHsl(value);
  if (!parsed) return null;
  return hslToHex(parsed.h, parsed.s, parsed.l);
}

function hslToHex(h, s, l) {
  const sat = s / 100;
  const light = l / 100;
  const a = sat * Math.min(light, 1 - light);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = light - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Closest accent-hue colour that stays at least 4.5:1 on the page background.
 * Light themes darken the accent. Dark themes keep a bright accent.
 */
export function readableAccentText(accentHsl, backgroundHsl) {
  const accent = parseHsl(accentHsl);
  const background = parseHsl(backgroundHsl);
  if (!accent || !background) return null;
  const bgHex = hslToHex(background.h, background.s, background.l);
  let best = null;
  for (let light = 8; light <= 92; light += 1) {
    const hex = hslToHex(accent.h, accent.s, light);
    const ratio = contrastRatio(hex, bgHex);
    const distance = Math.abs(light - accent.l);
    if (ratio < MIN_CONTRAST) continue;
    if (!best || distance < best.distance) best = { hex, ratio, distance };
  }
  return best ? best.hex : hslToHex(accent.h, accent.s, accent.l);
}
